import { Link } from "react-router-dom";
import { topEarners } from "@/mocks/social";

const platformIcon: Record<string, string> = {
  tiktok: "ri-tiktok-line",
  youtube: "ri-youtube-line",
  instagram: "ri-instagram-line",
  facebook: "ri-facebook-circle-line",
};

const platformColor: Record<string, string> = {
  tiktok: "text-gray-900",
  youtube: "text-red-500",
  instagram: "text-pink-500",
  facebook: "text-amber-700",
};

const topFactories = [
  { name: "TechVision Factory", id: "techvision", category: "Electronics", commission: "12%" },
  { name: "PureGlow Labs", id: "pureglow", category: "Beauty", commission: "15%" },
  { name: "FitForge Mfg", id: "fitforge", category: "Sports", commission: "10%" },
  { name: "EcoKitchen Works", id: "ecokitchen", category: "Kitchen", commission: "13%" },
];

interface SocialRightSidebarProps {
  onShareProduct: () => void;
}

export default function SocialRightSidebar({ onShareProduct }: SocialRightSidebarProps) {
  return (
    <aside className="hidden xl:block w-72 flex-shrink-0 space-y-4">
      {/* This Week's Top Earners */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-extrabold text-gray-900 text-sm">Top Earners This Week</h3>
          <span className="text-xs text-orange-500 font-semibold">Live</span>
        </div>
        <div className="space-y-3">
          {topEarners.map((earner, i) => (
            <div key={earner.handle} className="flex items-center gap-3">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-orange-50 text-orange-500 text-xs font-extrabold flex-shrink-0">
                {i + 1}
              </div>
              <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                <img src={earner.avatar} alt={earner.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-gray-900 truncate">{earner.name}</div>
                <div className={`flex items-center gap-1 text-xs ${platformColor[earner.platform]}`}>
                  <i className={platformIcon[earner.platform]} />
                  <span className="text-gray-400">{earner.followers}</span>
                </div>
              </div>
              <div className="text-sm font-extrabold text-green-600 flex-shrink-0">{earner.earned}</div>
            </div>
          ))}
        </div>
        <button
          onClick={onShareProduct}
          className="w-full mt-4 py-2.5 bg-orange-500 text-white text-sm font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-money-dollar-circle-line mr-1" /> Start Earning Too
        </button>
      </div>

      {/* How to Earn */}
      <div
        className="rounded-2xl p-5 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #fff8e1 0%, #fffde7 100%)" }}
      >
        <h3 className="font-extrabold text-amber-900 text-sm mb-3">How to Earn Commission</h3>
        <div className="space-y-3">
          {[
            { step: "1", text: "Pick a verified factory product", icon: "ri-store-2-line" },
            { step: "2", text: "Share to YouTube, TikTok, Instagram", icon: "ri-share-forward-line" },
            { step: "3", text: "Earn commission on every sale", icon: "ri-money-dollar-circle-line" },
          ].map((item) => (
            <div key={item.step} className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-orange-500 text-white text-xs font-extrabold flex-shrink-0">
                {item.step}
              </div>
              <div className="flex items-center gap-2 text-sm text-amber-900 font-semibold">
                <i className={`${item.icon} text-amber-600`} />
                {item.text}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center">
          <span className="text-2xl font-extrabold text-amber-800">Up to 15%</span>
          <div className="text-xs text-amber-700 font-semibold">Commission per sale</div>
        </div>
      </div>

      {/* Top Factories to Share */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 text-sm mb-4">Top Factories to Share</h3>
        <div className="space-y-2">
          {topFactories.map((f) => (
            <Link
              key={f.id}
              to={`/factory/${f.id}`}
              className="flex items-center gap-3 py-2.5 px-2 rounded-xl hover:bg-orange-50 transition-colors cursor-pointer"
            >
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center text-orange-400 flex-shrink-0">
                <i className="ri-store-2-line" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{f.name}</div>
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-shield-check-fill text-green-500" /> {f.category}
                </div>
              </div>
              <span className="text-xs font-extrabold text-green-600 flex-shrink-0">{f.commission}</span>
            </Link>
          ))}
        </div>
        <button
          onClick={onShareProduct}
          className="w-full mt-3 py-2 border border-orange-300 text-orange-500 text-sm font-bold rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          Browse All Products
        </button>
      </div>

      {/* Platform Links */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 text-sm mb-3">Share to Your Fans</h3>
        <div className="grid grid-cols-2 gap-2">
          {[
            { icon: "ri-youtube-line", label: "YouTube", color: "text-red-500", bg: "bg-red-50" },
            { icon: "ri-tiktok-line", label: "TikTok", color: "text-gray-900", bg: "bg-gray-50" },
            { icon: "ri-instagram-line", label: "Instagram", color: "text-pink-500", bg: "bg-pink-50" },
            { icon: "ri-facebook-circle-line", label: "Facebook", color: "text-amber-700", bg: "bg-amber-50" },
          ].map((p) => (
            <div
              key={p.label}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl ${p.bg} cursor-pointer hover:opacity-80 transition-opacity`}
            >
              <div className={`w-5 h-5 flex items-center justify-center ${p.color}`}>
                <i className={p.icon} />
              </div>
              <span className="text-xs font-semibold text-gray-700">{p.label}</span>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}
